/**
 * App-wide link health: ONE `RELINKING…` pill for every socket the page has open.
 *
 * `/ws/game` (the session), `/ws/roster` and `/ws/encounter` reconnect independently, each with
 * its own backoff. A wifi blip drops all of them at once, so the signal has to aggregate: the pill
 * shows while ANY socket that was live is down, escalates to `STILL RELINKING` + `RELINK NOW`
 * when any of them has spent its fast-retry budget, and the button revives every socket that is
 * down, or one room sits at a 15s backoff tail after the others healed.
 *
 * Rooms register through {@link useLinkReport} (called by `useRoomController`, so the roster and
 * encounter rooms both inherit it). Registration writes to an external store, not React state in a
 * context value, so a socket flapping re-renders only the pill, never the board that registered it.
 * With no {@link LinkHealthProvider} above (unit tests, `/scratch`), registration is a no-op.
 *
 * The session's own link state is read straight off the session by {@link LinkPill}.
 */
import { createContext, useContext, useEffect, useId, useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import { useGameSessionContext } from "./game/GameSessionProvider";
import { LINK_DOWN_NOTE } from "./roomWs";
import { useDelayedFlag } from "./useDelayedFlag";
import "./link-pill.css";

/** What one room reports: is its socket down after having been live, has the drop outlasted the fast budget, and how to revive it now. */
export type LinkReport = { down: boolean; lost: boolean; reconnect: () => void };

/** How long every socket may be down before the pill shows: a sub-second blip shows nothing. */
export const LINK_PILL_DELAY_MS = 1000;

class LinkHealthStore {
  private reports = new Map<string, LinkReport>();
  private listeners = new Set<() => void>();
  private snapshot: LinkReport[] = [];

  set(id: string, report: LinkReport | null): void {
    if (report === null) this.reports.delete(id);
    else this.reports.set(id, report);
    this.snapshot = [...this.reports.values()];
    for (const l of this.listeners) l();
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): LinkReport[] => this.snapshot;
}

const LinkHealthContext = createContext<LinkHealthStore | null>(null);

/** Hosts the registry the rooms report into. `RequireAuth` mounts it around every guarded surface. */
export function LinkHealthProvider({ children }: { children: ReactNode }) {
  const [store] = useState(() => new LinkHealthStore());
  return <LinkHealthContext.Provider value={store}>{children}</LinkHealthContext.Provider>;
}

/**
 * Report one room's link to the app-wide pill for as long as the caller is mounted. `reconnect`
 * is read through a ref, so an unstable callback never re-registers.
 */
export function useLinkReport(down: boolean, lost: boolean, reconnect: () => void): void {
  const store = useContext(LinkHealthContext);
  const id = useId();
  const reconnectRef = useRef(reconnect);
  reconnectRef.current = reconnect;
  useEffect(() => {
    if (store === null) return;
    store.set(id, { down, lost, reconnect: () => reconnectRef.current() });
  }, [store, id, down, lost]);
  useEffect(() => {
    if (store === null) return;
    return () => store.set(id, null);
  }, [store, id]);
}

const EMPTY: LinkReport[] = [];
const noSubscribe = () => () => {};

/**
 * The one link pill. Hidden while every socket is connected; `RELINKING…` after
 * {@link LINK_PILL_DELAY_MS} of any socket down; `STILL RELINKING` + `RELINK NOW` once any
 * socket has spent its fast-retry budget.
 *
 * **Never tell the user to RELOAD here.** Every socket retries indefinitely, so a reload would
 * destroy a GM's mid-fight session moments before the app heals itself. The escalated state KEEPS
 * the pulsing dot (it means "in progress", and under indefinite retry something genuinely still
 * is) and carries its escalation in EMPHASIS rather than ALARM: an alarm treatment on a
 * self-healing condition trains a GM to ignore the one treatment that should mean "act now".
 * `role="status"`/`polite` for the same reason; the button is what turns this from information
 * into agency.
 */
export function LinkPill() {
  const session = useGameSessionContext();
  const store = useContext(LinkHealthContext);
  const rooms = useSyncExternalStore(store?.subscribe ?? noSubscribe, store?.getSnapshot ?? (() => EMPTY));
  const down = session.linkDown || rooms.some((r) => r.down);
  const lost = session.linkStale || rooms.some((r) => r.lost);
  const show = useDelayedFlag(down, LINK_PILL_DELAY_MS);

  const reconnectAll = (): void => {
    if (session.linkDown) session.reconnect();
    for (const r of rooms) if (r.down) r.reconnect();
  };

  if (down && lost) {
    return (
      <div className="lvtt-link-pill lvtt-link-pill--lost" role="status" aria-live="polite">
        <span className="lvtt-link-dot" aria-hidden />
        STILL RELINKING
        <button type="button" className="lvtt-relink-btn" onClick={reconnectAll}>
          RELINK NOW
        </button>
      </div>
    );
  }
  if (!show) return null;
  return (
    <div className="lvtt-link-pill" role="status" aria-live="polite">
      <span className="lvtt-link-dot" aria-hidden />
      RELINKING…
    </div>
  );
}

/** How long the inline "not sent" note stays up after a refused write. */
const NOT_SENT_NOTE_MS = 4200;

/**
 * The inline note for a desktop form whose write the link refused (a session writer returning
 * `false`). `report(sent)` takes the writer's result and returns it, so a caller writes
 * `if (report(session.postChat(text))) setDraft("")` and keeps its input on a refusal. A later
 * successful send clears the note early.
 */
export function useNotSentNote(): { notSent: boolean; report: (sent: boolean) => boolean } {
  const [notSent, setNotSent] = useState(false);
  useEffect(() => {
    if (!notSent) return;
    const t = setTimeout(() => setNotSent(false), NOT_SENT_NOTE_MS);
    return () => clearTimeout(t);
  }, [notSent]);
  const report = (sent: boolean): boolean => {
    setNotSent(!sent);
    return sent;
  };
  return { notSent, report };
}

/** The "Link down — not sent" line a form shows under its control while {@link useNotSentNote} is up. */
export function NotSentNote({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <p className="lvtt-link-note" role="status">
      {LINK_DOWN_NOTE}
    </p>
  );
}
